import React, { useState } from 'react';
import SEO from '../../components/SEO';
import seoData from '../../config/seoData';
import { Helmet } from 'react-helmet-async';
import { Phone, MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const CustoLimpezaPosObra = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre custos de limpeza pós-obra e gostaria de um orçamento.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  // Structured Data - Article
  const articleData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": seoData['/blog/custo-limpeza-pos-obra'].title,
    "description": seoData['/blog/custo-limpeza-pos-obra'].description,
    "author": {
      "@type": "Organization",
      "name": "WellTec"
    },
    "publisher": {
      "@type": "Organization",
      "name": "WellTec",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png"
      }
    },
    "datePublished": "2024-08-15",
    "dateModified": "2024-12-15"
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={seoData['/blog/custo-limpeza-pos-obra'].title}
        description={seoData['/blog/custo-limpeza-pos-obra'].description}
        canonical={seoData['/blog/custo-limpeza-pos-obra'].canonical}
        article={true}
        publishedTime="2024-08-15"
        modifiedTime="2024-12-15"
      />

      {/* Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(articleData)}
        </script>
      </Helmet>
      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o Blog
          </button>

          {/* Article Header */}
          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Quanto Custa uma Limpeza Pós-Obra em BH? Preços e Estimativas por m²
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>8 min de leitura</span>
              </div>
            </div>
          </header>

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Você acabou de concluir uma obra ou reforma e agora se pergunta: <strong>quanto vai custar fazer a limpeza pós-obra?</strong> Este guia completo vai te ajudar a entender todos os fatores que influenciam no preço e como calcular um orçamento realista.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Fatores que Influenciam o Preço da Limpeza Pós-Obra
            </h2>

            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">1. Tamanho da Área</h3>
                <p className="text-gray-700 mb-4">
                  O principal fator é a metragem. Geralmente, cobra-se por m²:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Áreas pequenas</strong> (até 100m²): R$ 8-12 por m²</li>
                  <li><strong>Áreas médias</strong> (100-500m²): R$ 6-10 por m²</li>
                  <li><strong>Áreas grandes</strong> (500m²+): R$ 4-8 por m²</li>
                </ul>
              </CardContent>
            </Card>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              2. Nível de Sujeira e Tipo de Resíduos
            </h3>
            <p className="text-gray-700 mb-4">
              Obras com muito cimento, rejunte, tinta ou massa corrida exigem mais tempo e produtos especializados, aumentando o custo em 20-40%.
            </p>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              3. Tipo de Imóvel
            </h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Residencial:</strong> R$ 500 - R$ 2.000 (apartamentos/casas)</li>
              <li><strong>Comercial:</strong> R$ 1.500 - R$ 5.000 (lojas, escritórios)</li>
              <li><strong>Industrial:</strong> R$ 3.000 - R$ 15.000+ (galpões, fábricas)</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              4. Urgência e Prazo
            </h3>
            <p className="text-gray-700 mb-6">
              Serviços urgentes (menos de 48h) podem ter acréscimo de 30-50% no valor.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tabela de Preços Médios (Região de BH - 2025)
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Tipo de Serviço</th>
                    <th className="p-4 text-left">Faixa de Preço</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4">Apartamento 2 quartos</td>
                    <td className="p-4 font-semibold">R$ 800 - R$ 1.500</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4">Casa 3 quartos</td>
                    <td className="p-4 font-semibold">R$ 1.200 - R$ 2.500</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4">Loja comercial (100m²)</td>
                    <td className="p-4 font-semibold">R$ 800 - R$ 1.500</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4">Galpão industrial (500m²)</td>
                    <td className="p-4 font-semibold">R$ 3.000 - R$ 6.000</td>
                  </tr>
                  <tr>
                    <td className="p-4">Condomínio (áreas comuns)</td>
                    <td className="p-4 font-semibold">R$ 2.000 - R$ 5.000</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              O que Está Incluído no Serviço?
            </h2>
            <p className="text-gray-700 mb-4">
              Um serviço profissional de limpeza pós-obra deve incluir:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Remoção de entulho leve e resíduos</li>
              <li>Limpeza de pisos (remoção de cimento, rejunte, cola)</li>
              <li>Limpeza de vidros e esquadrias</li>
              <li>Limpeza de paredes e rodapés</li>
              <li>Aspiração de pó fino</li>
              <li>Limpeza de áreas molhadas (banheiros, cozinha)</li>
            </ul>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">💡 Dica Importante</h3>
                <p className="text-gray-700">
                  Sempre solicite uma <strong>visita técnica gratuita</strong>. Um orçamento feito sem avaliar o local pode gerar surpresas desagradáveis com cobranças extras.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Vale a Pena Contratar Profissionais?
            </h2>
            <p className="text-gray-700 mb-4">
              Sim! Uma limpeza pós-obra profissional economiza tempo e garante resultado superior:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Equipamentos industriais:</strong> Lavadoras de piso, aspiradores profissionais, varredeiras</li>
              <li><strong>Produtos especializados:</strong> Removedores de cimento, desengraxantes industriais</li>
              <li><strong>Equipe treinada:</strong> Técnicas corretas para cada tipo de sujeira</li>
              <li><strong>Garantia de resultado:</strong> Obra entregue limpa e pronta para uso</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Como Economizar na Limpeza Pós-Obra
            </h2>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 mb-6">
              <li>Remova você mesmo o entulho pesado antes</li>
              <li>Agende com antecedência (evite urgências)</li>
              <li>Faça uma varredura básica prévia</li>
              <li>Compare orçamentos de pelo menos 3 empresas</li>
              <li>Negocie pacotes para múltiplos imóveis</li>
            </ol>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Precisa de um Orçamento de Limpeza Pós-Obra?</h3>
                <p className="mb-6">
                  A WellTec oferece orçamento gratuito com visita técnica em BH e região metropolitana
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Orçamento Gratuito
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Related Articles */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Artigos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/limpeza-pos-obra-residencial')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Limpeza Pós-Obra Residencial: Checklist Completo</h4>
                  <p className="text-gray-600 text-sm">Saiba tudo que deve ser limpo após uma reforma residencial</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/limpeza-vs-convencional')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Limpeza Pós-Obra vs Limpeza Convencional</h4>
                  <p className="text-gray-600 text-sm">Entenda as diferenças e quando contratar cada serviço</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em BH e região</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
                <li><button onClick={handleWhatsAppClick} className="hover:text-green-400">WhatsApp</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <button 
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default CustoLimpezaPosObra;
